import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { FadeUp, LineReveal } from '../components/Reveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export default function Pricing() {
  const { t } = useLang();
  const [amount, setAmount] = useState(49);

  const cards = [
    { tKey: 'free', tag: t('pricingPage.freeTag'), title: t('pricingPage.freeTitle'), desc: t('pricingPage.freeDesc') },
    { tKey: 'predef', tag: t('pricingPage.predefTag'), title: t('pricingPage.predefTitle'), desc: t('pricingPage.predefDesc') },
    { tKey: 'credits', tag: t('pricingPage.creditsTag'), title: t('pricingPage.creditsTitle'), desc: t('pricingPage.creditsDesc'), accent: true },
  ];

  return (
    <div>
      <section className="bg-[#10282C] grain relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">{t('pricingPage.kicker')}</p>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl" data-testid="pricing-headline">
            <LineReveal lines={[t('pricingPage.title')]} />
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">{t('pricingPage.sub')}</p>
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cards.map((c, i) => (
              <FadeUp key={c.tKey} delay={i * 0.08}>
                <div
                  data-testid={`pricing-card-${c.tKey}`}
                  className={`h-full rounded-2xl p-8 border ${c.accent ? 'bg-[#10282C] border-[#00C5C2]/30' : 'bg-white border-[#10282C]/8'}`}
                >
                  <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-mono font-bold tracking-wide ${c.accent ? 'bg-[#FD584F] text-white' : 'bg-[#00918F]/10 text-[#00918F]'}`}>
                    {c.tag}
                  </span>
                  <h2 className={`mt-5 font-display text-xl font-semibold ${c.accent ? 'text-white' : 'text-[#10282C]'}`}>{c.title}</h2>
                  <p className={`mt-3 text-sm leading-relaxed ${c.accent ? 'text-white/60' : 'text-[#10282C]/60'}`}>{c.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-8 flex items-center gap-3 rounded-xl bg-[#00C5C2]/8 border border-[#00918F]/20 px-5 py-4">
            <Check size={17} className="text-[#00918F] shrink-0" />
            <p className="text-sm text-[#10282C]/75">{t('pricingPage.supportNote')}</p>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 bg-white border-y border-[#10282C]/6">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#10282C]">{t('pricingPage.tableTitle')}</h2>
            <div className="mt-3 flex items-center gap-2 text-[13px] text-[#b06a00]">
              <AlertTriangle size={14} />
              <span>{t('pricingPage.tableNote')}</span>
            </div>
            <div className="mt-8 rounded-2xl border border-[#10282C]/10 overflow-hidden" data-testid="pricing-rate-table">
              <table className="w-full text-left">
                <thead className="bg-[#10282C]">
                  <tr>
                    <th className="px-6 py-3.5 text-[12px] font-mono tracking-wider text-[#00C5C2] uppercase">{t('pricingPage.tableCol1')}</th>
                    <th className="px-6 py-3.5 text-[12px] font-mono tracking-wider text-[#00C5C2] uppercase text-right">{t('pricingPage.tableCol2')}</th>
                  </tr>
                </thead>
                <tbody>
                  {['row1', 'row2', 'row3', 'row4'].map((r, i) => (
                    <tr key={r} className={i % 2 ? 'bg-[#F6F8F7]' : 'bg-white'}>
                      <td className="px-6 py-4 text-sm text-[#10282C]/75">{t(`pricingPage.${r}`)}</td>
                      <td className="px-6 py-4 text-sm font-mono text-[#10282C]/40 text-right">{t('pricingPage.tbd')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp className="rounded-3xl bg-[#10282C] grain relative overflow-hidden p-9 sm:p-12">
            <div className="relative z-10">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">{t('pricingPage.calcTitle')}</h2>
              <p className="mt-2.5 text-sm text-white/55">{t('pricingPage.calcSub')}</p>
              <div className="mt-9">
                <div className="flex items-end justify-between">
                  <span className="font-display text-5xl font-bold text-[#00C5C2]" data-testid="credit-amount-display">${amount}</span>
                  <span className="text-sm font-mono text-white/50">{amount * 100} {t('pricingPage.calcCredits')}</span>
                </div>
                <input
                  data-testid="credit-slider"
                  type="range"
                  min="49"
                  max="500"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="credit-slider w-full mt-6"
                  aria-label={t('pricingPage.calcTitle')}
                />
                <div className="mt-2 flex justify-between text-[11px] font-mono text-white/35">
                  <span>$49 min</span>
                  <span>$500</span>
                </div>
              </div>
              <Link to="/signup" data-testid="pricing-signup-cta" className="mt-9 btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
                {t('pricingPage.cta')} <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>

          <FadeUp className="mt-8 rounded-2xl bg-white border border-[#10282C]/8 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <h3 className="font-display text-lg font-semibold text-[#10282C]">{t('pricingPage.entTitle')}</h3>
              <p className="mt-2 text-sm text-[#10282C]/60 leading-relaxed max-w-md">{t('pricingPage.entSub')}</p>
            </div>
            <Link to="/enterprise" data-testid="pricing-enterprise-cta" className="btn-teal shrink-0 px-6 py-3 rounded-full text-[14px] font-semibold">
              {t('pricingPage.entCta')}
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-[#10282C]/6">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#10282C]">{t('pricingPage.faqTitle')}</h2>
            <span className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FD584F]/10 text-[#c23a32] text-[11px] font-mono">
              <AlertTriangle size={11} /> {t('pricingPage.needsInput')}
            </span>
            <Accordion type="single" collapsible className="mt-7">
              {[1, 2, 3].map((n) => (
                <AccordionItem key={n} value={`bq${n}`}>
                  <AccordionTrigger data-testid={`billing-faq-${n}`} className="text-left font-display text-[15px] font-semibold text-[#10282C]">
                    {t(`pricingPage.bq${n}`)}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#10282C]/60 leading-relaxed">
                    {t(`pricingPage.ba${n}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
